import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Avatar, IconButton, SidebarItem, ThemeToggle, Toast, tripColorFor, tripHueColor, vars } from "@pack-it-up/ui-web";
import type { ThemeMode } from "@pack-it-up/ui-web";
import { useStoreValue } from "@pack-it-up/viewmodel/bindings";
import type { Theme } from "@pack-it-up/viewmodel";
import type { AuthUser, TripSummary } from "@pack-it-up/core";
import { useStores } from "./AppContext.js";
import { useSession } from "./SessionProvider.js";
import { APP_NAME } from "../constants.js";
import { ArchiveIcon, FriendsIcon, HomeIcon, InvitesIcon, LogoMarkIcon, MenuIcon, MyTripsIcon, SettingsIcon, TemplatesIcon } from "./icons.js";
import * as styles from "./AppShell.css.js";

// Sidebar is user-resizable (drag the divider); clamped so it never collapses to nothing
// or eats the page. Below NARROW_PX it becomes an off-canvas drawer behind a hamburger.
const MIN_WIDTH = 200;
const MAX_WIDTH = 420;
const DEFAULT_WIDTH = 240;
const NARROW_PX = 820;
const WIDTH_KEY = "pk.sidebarWidth";

const clampWidth = (w: number): number => Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, w));

const readStoredWidth = (): number => {
  if (typeof window === "undefined") return DEFAULT_WIDTH;
  const raw = window.localStorage?.getItem(WIDTH_KEY);
  const n = raw !== null && raw !== undefined ? Number(raw) : NaN;
  return Number.isFinite(n) ? clampWidth(n) : DEFAULT_WIDTH;
};

// Tracks whether the viewport is below the narrow breakpoint (drawer mode).
const useNarrow = (): boolean => {
  const query = `(max-width: ${NARROW_PX}px)`;
  const [narrow, setNarrow] = useState(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function" ? window.matchMedia(query).matches : false,
  );
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(query);
    const onChange = (): void => setNarrow(mq.matches);
    onChange();
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, [query]);
  return narrow;
};

const sectionLabel = (text: string): ReactNode => (
  <div
    style={{
      fontSize: vars.font.size2xs,
      fontWeight: vars.font.weightSemibold,
      letterSpacing: vars.font.trackingWide,
      textTransform: "uppercase",
      color: vars.color.textMuted,
      padding: "0 6px",
      marginBottom: "4px",
    }}
  >
    {text}
  </div>
);

// The sidebar's contents — reused by both the persistent desktop rail and the mobile
// drawer. `onNavigate` navigates and (in the drawer) closes it.
const SidebarBody = ({
  user,
  mode,
  upcoming,
  inviteCount,
  path,
  onNavigate,
  onSetTheme,
}: {
  user: AuthUser | null;
  mode: ThemeMode;
  upcoming: TripSummary[];
  inviteCount: number;
  path: string;
  onNavigate: (to: string) => void;
  onSetTheme: (m: ThemeMode) => void;
}): ReactNode => {
  const isActive = (to: string): boolean => (to === "/" ? path === "/" : path.startsWith(to));
  const isAnonymous = user !== null && user.isAnonymous;
  return (
    <>
      <div style={{ display: "flex", alignItems: "center", borderBottom: `1px solid ${vars.color.border}` }}>
        <button type="button" className={styles.logoBlock} onClick={() => onNavigate("/")}>
          <span
            style={{
              width: "32px",
              height: "32px",
              borderRadius: vars.radius.sm,
              background: vars.color.accent,
              color: vars.color.accentText,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            aria-hidden
          >
            <LogoMarkIcon size={20} />
          </span>
          <span style={{ flex: 1, fontFamily: vars.font.heading, fontWeight: vars.font.weightBold, fontSize: vars.font.sizeXl, letterSpacing: vars.font.trackingSnug }}>
            {APP_NAME}
          </span>
        </button>
      </div>

      <nav style={{ flex: 1, overflowY: "auto" }}>
        <div style={{ padding: "16px 12px 8px" }}>
          {sectionLabel("Navigation")}
          <SidebarItem icon={HomeIcon} active={isActive("/")} onClick={() => onNavigate("/")}>
            Home
          </SidebarItem>
          <SidebarItem icon={MyTripsIcon} active={isActive("/trips")} onClick={() => onNavigate("/trips")}>
            My Trips
          </SidebarItem>
          <SidebarItem icon={TemplatesIcon} active={isActive("/templates")} onClick={() => onNavigate("/templates")}>
            My Templates
          </SidebarItem>
          {!isAnonymous && (
            <>
              <SidebarItem icon={FriendsIcon} active={isActive("/friends")} onClick={() => onNavigate("/friends")}>
                Friends
              </SidebarItem>
              {/* The post-sign-in nudge (T25 §1.4): a count badge, never an auto-accept. */}
              <SidebarItem
                icon={InvitesIcon}
                active={isActive("/invites")}
                {...(inviteCount > 0 ? { badge: inviteCount } : {})}
                onClick={() => onNavigate("/invites")}
              >
                Invites
              </SidebarItem>
              <SidebarItem icon={SettingsIcon} active={isActive("/settings")} onClick={() => onNavigate("/settings")}>
                Settings
              </SidebarItem>
            </>
          )}
        </div>

        {upcoming.length > 0 && (
          <div style={{ padding: "16px 12px 8px" }}>
            {sectionLabel("Upcoming Trips")}
            {upcoming.map((t) => (
              <SidebarItem
                key={t.id}
                dotColor={tripHueColor[t.color ?? tripColorFor(t.id)]}
                active={path.startsWith(`/trip/${t.id}`)}
                onClick={() => onNavigate(`/trip/${t.id}`)}
              >
                {t.name}
              </SidebarItem>
            ))}
          </div>
        )}
      </nav>

      <div style={{ borderTop: `1px solid ${vars.color.border}`, padding: vars.space.md, display: "flex", flexDirection: "column", alignItems: "center", gap: vars.space.sm }}>
        <ThemeToggle value={mode} onChange={onSetTheme} />
        {user !== null && (
          <button type="button" className={styles.userRow} onClick={() => onNavigate(isAnonymous ? "/signup" : "/settings")}>
            <Avatar name={isAnonymous ? "Anonymous" : user.displayName} size="sm" />
            <span style={{ minWidth: 0 }}>
              <span style={{ display: "block", fontSize: vars.font.sizeSm, fontWeight: vars.font.weightMedium, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {isAnonymous ? "Anonymous" : user.displayName}
              </span>
              {!isAnonymous && (
                <span style={{ display: "block", fontSize: vars.font.sizeXs, color: vars.color.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {user.email}
                </span>
              )}
            </span>
          </button>
        )}
      </div>
    </>
  );
};

const asideStyle = { display: "flex", flexDirection: "column", background: vars.color.surface, overflow: "hidden", height: "100%" } as const;

// The persistent sidebar + detail layout (design system / Doc 10). On wide viewports the
// sidebar is a resizable rail; on narrow ones it collapses to a hamburger-triggered
// drawer. Signed-in routes render into the detail pane either way.
export const AppShell = ({ children }: { children: ReactNode }): ReactNode => {
  const { auth, theme } = useStores();
  const { home, inviteInbox, pendingTemplateDelete, pendingTripDelete } = useSession();
  const location = useLocation();
  const navigate = useNavigate();

  const user = useStoreValue(auth, (s) => s.user);
  const mode = useStoreValue(theme, (s) => s.theme) as ThemeMode;
  const trips = useStoreValue(home, (s) => s.trips);
  const inviteCount = useStoreValue(inviteInbox, (s) => s.invites.length);
  // groups() returns a fresh object each call — memoize off the stable `trips` array.
  const upcoming = useMemo(() => home.getState().groups().upcoming, [home, trips]);
  // Delete-template/delete-trip Undo toasts (§ TemplateEditor / § TripDetailPage):
  // armed from the entity's own page, but shown here since the delete navigates away
  // from that page right away. Both can be up at once (rare — deleting one of each
  // within the same Undo window), so they're stacked rather than exclusive.
  const pendingTemplate = useStoreValue(pendingTemplateDelete, (s) => s.pending);
  const pendingTrip = useStoreValue(pendingTripDelete, (s) => s.pending);

  const narrow = useNarrow();
  const [width, setWidth] = useState(readStoredWidth);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const resizing = useRef(false);

  // Persist the chosen width across sessions.
  useEffect(() => {
    if (typeof window !== "undefined") window.localStorage?.setItem(WIDTH_KEY, String(width));
  }, [width]);

  const setTheme = (m: ThemeMode): void => void theme.getState().setTheme(m as Theme);
  const go = useCallback(
    (to: string): void => {
      navigate(to);
      setDrawerOpen(false);
    },
    [navigate],
  );

  // Pointer drag on the divider resizes the rail; listeners live on window so the drag
  // continues even if the pointer outruns the 6px handle.
  const startResize = (e: PointerEvent): void => {
    e.preventDefault();
    resizing.current = true;
    const startX = e.clientX;
    const startW = width;
    const onMove = (ev: globalThis.PointerEvent): void => {
      if (resizing.current) setWidth(clampWidth(startW + (ev.clientX - startX)));
    };
    const onUp = (): void => {
      resizing.current = false;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  // Keyboard resize for a11y (the divider is focusable).
  const onDividerKey = (e: KeyboardEvent): void => {
    if (e.key === "ArrowLeft") setWidth((w) => clampWidth(w - 16));
    else if (e.key === "ArrowRight") setWidth((w) => clampWidth(w + 16));
  };

  const pendingDeleteToast = (pendingTemplate !== null || pendingTrip !== null) && (
    <div
      style={{
        position: "fixed",
        bottom: vars.space.lg,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 100,
        display: "flex",
        flexDirection: "column",
        gap: vars.space.sm,
        alignItems: "center",
      }}
    >
      {pendingTemplate !== null && (
        <Toast
          message={`Deleting "${pendingTemplate.name}"…`}
          onUndo={() => pendingTemplateDelete.getState().undo()}
        />
      )}
      {pendingTrip !== null && (
        <Toast
          message={`Deleting "${pendingTrip.name}"…`}
          onUndo={() => pendingTripDelete.getState().undo()}
        />
      )}
    </div>
  );

  const body = (
    <SidebarBody
      user={user}
      mode={mode}
      upcoming={upcoming}
      inviteCount={inviteCount}
      path={location.pathname}
      onNavigate={go}
      onSetTheme={setTheme}
    />
  );

  // Narrow: top bar with a hamburger + an off-canvas drawer and backdrop.
  if (narrow) {
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100vh", background: vars.color.background }}>
        <div style={{ display: "flex", alignItems: "center", gap: vars.space.sm, padding: vars.space.sm, borderBottom: `1px solid ${vars.color.border}`, background: vars.color.surface }}>
          <IconButton label="Open menu" onClick={() => setDrawerOpen(true)}>
            {MenuIcon}
          </IconButton>
          <button type="button" className={styles.logoBlock} style={{ padding: 0 }} onClick={() => go("/")}>
            <span
              style={{
                width: "28px",
                height: "28px",
                borderRadius: vars.radius.sm,
                background: vars.color.accent,
                color: vars.color.accentText,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
              aria-hidden
            >
              <LogoMarkIcon size={16} />
            </span>
            <span style={{ fontFamily: vars.font.heading, fontWeight: vars.font.weightBold, fontSize: vars.font.sizeLg, letterSpacing: vars.font.trackingSnug }}>{APP_NAME}</span>
          </button>
        </div>
        {drawerOpen && (
          <>
            <div
              onClick={() => setDrawerOpen(false)}
              style={{ position: "fixed", inset: 0, background: vars.color.overlay, zIndex: 40 }}
              aria-hidden
            />
            <aside style={{ ...asideStyle, position: "fixed", top: 0, left: 0, bottom: 0, width: "280px", maxWidth: "85vw", borderRight: `1px solid ${vars.color.border}`, zIndex: 50 }}>
              {body}
            </aside>
          </>
        )}
        <main style={{ flex: 1, overflowY: "auto" }}>{children}</main>
        {pendingDeleteToast}
      </div>
    );
  }

  // Wide: a resizable rail + detail pane.
  return (
    <div style={{ display: "grid", gridTemplateColumns: `${width}px 1fr`, height: "100vh", background: vars.color.background }}>
      <aside style={{ ...asideStyle, position: "relative", borderRight: `1px solid ${vars.color.border}` }}>
        {body}
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize sidebar"
          tabIndex={0}
          onPointerDown={startResize}
          onKeyDown={onDividerKey}
          style={{ position: "absolute", top: 0, right: "-3px", width: "6px", height: "100%", cursor: "col-resize", zIndex: 10 }}
        />
      </aside>
      <main style={{ overflowY: "auto" }}>{children}</main>
      {pendingDeleteToast}
    </div>
  );
};
